import React, { useState, useEffect } from 'react';
import SmartCardBusiness from '../Templates/SmartCardBusiness';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../../modules/auth/firebaseConfig';

const rawCountries = [
  "United States", "India", "Tanzania", "Canada", "United Kingdom", "Germany",
  "France", "Australia", "Singapore", "UAE", "Japan", "China",
  "South Africa", "Nigeria"
];

const countries = rawCountries.map(name =>
  name.length > 16 ? name.split(/\s|-/).map(word => word[0]).join('').toUpperCase() : name
);

const categoryRoles = {
  'Traders': ['Retail Forex Trader', 'Retail Stock Trader', 'Retail Crypto Trader'],
  'Signal Providers': ['Forex Signal Provider', 'Stock Signal Provider'],
  'Others': ['Others']
};

export default function Step2_CreateProfile({ next, back }) {
  const [editMode, setEditMode] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [formData, setFormData] = useState({
    cardType: 'Business',
    businessName: '',
    publicName: '',
    shortName: '',
    tier: 'Silver',
    cardNumber: '',
    category: '',
    role1: '',
    country: 'United States',
    isVerified: false,
    isTrusted: false,
    qrCodeUrl: '',
    logoUrl: '/assets/images/m2t-logo.png',
    photoUrl: '',
    showLogo: true,
    status: 'On review',
    mode: 'Business'
  });

  const generateCardNumber = () => {
    const prefix = '9876';
    const middle = Math.floor(10000000 + Math.random() * 90000000).toString();
    const suffix = Math.floor(1000 + Math.random() * 9000).toString();
    return `${prefix} ${middle.slice(0, 4)} ${middle.slice(4)} ${suffix}`;
  };

  const generateQRCodeUrl = (cardNumber) => {
    const referralCode = cardNumber.replace(/\s/g, '').slice(-6);
    return `https://million2trillion.com/r/${referralCode}`;
  };

useEffect(() => {
  const storedEmail = localStorage.getItem('smartcardEmail');
  const storedName = localStorage.getItem('smartcardName');
  const storedCategory = localStorage.getItem('smartcardCategory');
  const storedRole = localStorage.getItem('smartcardRole');

  if (!storedEmail) {
    alert('No email found. Please go back to Step 1.');
    return;
  }

  setUserEmail(storedEmail);

  const fetchData = async () => {
    const ref = doc(db, 'smartcardData', storedEmail);
    const snap = await getDoc(ref);

    const cardNumber = generateCardNumber();
    const qrCodeUrl = generateQRCodeUrl(cardNumber);

    if (snap.exists()) {
      const data = snap.data();
      setFormData(prev => ({
        ...prev,
        ...data,
        cardNumber: data.cardNumber || cardNumber,
        qrCodeUrl: data.qrCodeUrl || qrCodeUrl,
        role1: data.role || storedRole || '',
        category: data.category || storedCategory || '',
        businessName: data.businessName || storedName || '',
        publicName: data.publicName || storedName || ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        email: storedEmail,
        name: storedName || '',
        role1: storedRole || '',
        category: storedCategory || '',
        businessName: storedName || '',
        publicName: storedName || '',
        cardNumber,
        qrCodeUrl
      }));
    }
  };

  fetchData();
}, []);



  const handleChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...formData, [name]: value };
    setFormData(updated);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setPhotoFile(file);
      setFormData(prev => ({ ...prev, photoUrl: url }));
    }
  };

  const handleGenerateCardNumber = async () => {
    const newCardNumber = generateCardNumber();
    const newQrCodeUrl = generateQRCodeUrl(newCardNumber);

    const updated = {
      ...formData,
      cardNumber: newCardNumber,
      qrCodeUrl: newQrCodeUrl
    };
    setFormData(updated);

    if (userEmail) {
      const ref = doc(db, 'smartcardData', userEmail);
      await setDoc(ref, {
        ...updated,
        currentStep: 2,
        timestamp: new Date().toLocaleString('en-GB', { timeZone: 'Africa/Dar_es_Salaam' })
      }, { merge: true });
    }
  };

const handleNext = async () => {
  if (!userEmail) {
    alert('Email not found. Please refresh.');
    return;
  }

  const ref = doc(db, 'smartcardData', userEmail);
  const updatedData = {
    email: userEmail,
    name: formData.name,
    cardType: formData.cardType,
    businessName: formData.businessName,
    publicName: formData.publicName,
    category: formData.category,
    industry: formData.category,
    role: formData.role1,
    role1: formData.role1,
    tier: formData.tier,
    country: formData.country,
    photoUrl: formData.photoUrl || '',
    cardNumber: formData.cardNumber,
    qrCodeUrl: formData.qrCodeUrl,
    currentStep: 2,
    timestamp: new Date().toLocaleString('en-GB', {
      timeZone: 'Africa/Dar_es_Salaam'
    }),
  };

  try {
    await setDoc(ref, updatedData, { merge: true });
    next(formData);
  } catch (err) {
    console.error('Error saving Step 2 data:', err);
    alert('Failed to save data. Please try again.');
  }
};



  const validity = formData.isVerified ? 'Lifetime' : '02/08/2026';
  const displayName = formData.cardType === 'Personal' ? formData.publicName : formData.businessName;

const left = (
  <div className="w-full lg:w-1/2 space-y-5 text-white">
    <h3 className="text-2xl font-bold">SmartCard Profile</h3>

    {/* Card Type */}
    <div>
      <label className="block mb-1">Card Type</label>
      <div className="flex gap-4">
        <label>
          <input type="radio" name="cardType" value="Personal" checked={formData.cardType === 'Personal'} onChange={handleChange} /> Personal
        </label>
        <label>
          <input type="radio" name="cardType" value="Business" checked={formData.cardType === 'Business'} onChange={handleChange} /> Business
        </label>
      </div>
    </div>

    {/* Business Name / Public Name */}
    <div>
      <label className="block mb-1">{formData.cardType === 'Personal' ? 'Public Name / Nickname' : 'Business Name'}</label>
      <input
        type="text"
        name={formData.cardType === 'Personal' ? 'publicName' : 'businessName'}
        className="w-full bg-black text-white border border-gray-700 rounded px-3 py-2"
        value={formData[formData.cardType === 'Personal' ? 'publicName' : 'businessName']}
        onChange={handleChange}
      />
    </div>

    {/* Tier */}
    <div>
      <label className="block mb-1">Tier</label>
      <select name="tier" value={formData.tier} onChange={handleChange} className="w-full bg-black text-white border border-gray-700 rounded px-3 py-2">
        {['Silver', 'Gold', 'Royal', 'Premium'].map(t => <option key={t}>{t}</option>)}
      </select>
    </div>

    {/* Industry */}
    <div>
      <label className="block mb-1 font-medium">Industry</label>
      <select name="category" value={formData.category} onChange={handleChange} className="w-full bg-black text-white border border-gray-700 rounded px-3 py-2">
        <option value="">Select Industry</option>
        {Object.keys(categoryRoles).map(cat => (
          <option key={cat} value={cat}>{cat}</option>
        ))}
      </select>
    </div>

    {/* Sub Category */}
    <div>
      <label className="block mb-1 font-medium">Sub Category</label>
      <select name="role1" value={formData.role1} onChange={handleChange} className="w-full bg-black text-white border border-gray-700 rounded px-3 py-2">
        <option value="">Select Sub Category</option>
        {formData.category && categoryRoles[formData.category]?.map(role => (
          <option key={role} value={role}>{role}</option>
        ))}
      </select>
    </div>

    {/* Country */}
    <div>
      <label className="block mb-1">Country</label>
      <select name="country" value={formData.country} onChange={handleChange} className="w-full bg-black text-white border border-gray-700 rounded px-3 py-2">
        {countries.map(c => <option key={c}>{c}</option>)}
      </select>
    </div>

    {/* Photo Upload */}
    <div>
      <label className="block mb-1">Upload Photo</label>
      <input type="file" accept="image/*" onChange={handlePhotoChange} className="w-full text-white" />
      <p className="text-gray-400 text-sm mt-1">Default: M2T logo if no image</p>
    </div>

    {/* Back and Next Buttons */}
    <div className="flex justify-between pt-4">
      <button onClick={back} className="bg-gray-700 px-6 py-2 rounded">Back</button>
      <button
  onClick={handleNext}
  className="bg-green-600 px-6 py-2 rounded"
>
  Next
</button>

    </div>

    <p className="text-green-500 text-sm mt-3">🎁 Free membership until October!</p>
  </div>
);


  const right = (
    <div className="w-full lg:w-1/2">
      <SmartCardBusiness
        businessName={displayName}
        industry={formData.category}
        subCategory={formData.role1}
        cardNumber={formData.cardNumber}
        tier={formData.tier}
        mode={formData.cardType}
        isVerified={formData.isVerified}
        isTrusted={formData.isTrusted}
        qrCodeUrl={formData.qrCodeUrl}
        logoUrl={formData.logoUrl}
        photoUrl={formData.photoUrl || '/assets/images/m2t-logo.png'}
        cardValidity={validity}
        businessCode={formData.shortName}
        showLogo={formData.showLogo}
        status={formData.status}
        country={formData.country}
      />
      <button onClick={handleGenerateCardNumber} className="mt-4 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded">
        Generate Card Number
      </button>

      <div className="mt-12 bg-black text-white border border-green-600 rounded-lg p-4 text-sm w-full max-w-md shadow-lg">
          <h3 className="text-lg font-semibold text-green-400 mb-2 text-center">🎁 Free Until October</h3>
          <p className="text-gray-300 text-center mb-3">
            Explore full platform features with <strong>no payment</strong> until October.
            Your access is fully unlocked — no credit card required.
          </p>
          <div className="grid grid-cols-2 gap-4 text-xs text-white">
            <div>
              <h4 className="font-semibold text-yellow-400 mb-1">Personal SmartCards</h4>
              <ul className="space-y-1">
                <li>🥈 Silver – <span className="text-green-500">Free</span></li>
                <li>🥇 Gold – <s>$19/mo</s> <span className="text-green-500">Free</span></li>
                <li>👑 Royal – <s>$49/mo</s> <span className="text-green-500">Free</span></li>
                <li>💎 Premium – <s>$99/mo</s> <span className="text-green-500">Free</span></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-yellow-400 mb-1">Business SmartCards</h4>
              <ul className="space-y-1">
                <li>🥈 Silver – <span className="text-green-500">Free</span></li>
                <li>🥇 Gold – <s>$129/mo</s> <span className="text-green-500">Free</span></li>
                <li>👑 Royal – <s>$399/mo</s> <span className="text-green-500">Free</span></li>
                <li>💎 Premium – <s>$999/mo</s> <span className="text-green-500">Free</span></li>
              </ul>
            </div>
          </div>
          <p className="text-center mt-4 text-yellow-300 font-semibold">
            🎯 Reserve your spot and access everything!
          </p>
        </div>

    </div>
  );

  return { left, right };
}